import { InputWrapperProps } from '@mantine/core';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { TextInput } from 'src/design/atoms/TextInput';
import { useLocalStorage } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { BasicButton } from 'src/design/atoms/BasicButton';

export interface ICheckboxListSelectorProps<T>
  extends Omit<InputWrapperProps, 'value' | 'onChange'> {
  items: T[];
  value: T[];
  onChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  getItemId: (item: T) => string | number;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  storageKey?: string;
  defaultToAll?: boolean;
}

/** @registry Generic checkbox list for selecting a subset of items of type T. */
export function CheckboxListSelector<T>({
  items,
  value,
  onChange,
  getItemLabel,
  getItemId,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  disabled = false,
  storageKey,
  defaultToAll = false,
  ...props
}: ICheckboxListSelectorProps<T>) {
  const [storedIds, setStoredIds] = useLocalStorage<(string | number)[]>({
    key: storageKey ?? 'checkbox-list-selector-temp',
    defaultValue: [],
  });
  const [search, setSearch] = useLocalStorage<string>({
    key: storageKey
      ? `${storageKey}-search`
      : 'checkbox-list-selector-temp-search',
    defaultValue: '',
  });
  const [initialized, setInitialized] = useState(!storageKey && !defaultToAll);

  // Restore selected items from local storage
  useEffect(() => {
    if (initialized || items.length === 0) return;

    // If there are stored items, restore them using the stored ids
    if (storageKey && storedIds.length > 0) {
      const restoredItems = items.filter((item) =>
        storedIds.includes(getItemId(item)),
      );
      if (restoredItems.length > 0) {
        onChange(restoredItems);
        setInitialized(true);
        return;
      }
    }

    // If no stored items and defaultToAll is set, select all items
    if (defaultToAll) {
      onChange(items);
    }

    setInitialized(true);
  }, [
    storageKey,
    storedIds,
    items,
    getItemId,
    onChange,
    initialized,
    defaultToAll,
  ]);

  // Save the selected items to local storage when the value changes
  useEffect(() => {
    // If there is no storage key or the component is not initialized, return because we local storage is not required
    if (!storageKey || !initialized) return;

    // Get the ids of the selected items
    const ids = value.map((item) => getItemId(item));
    setStoredIds(ids);
  }, [storageKey, value, getItemId, setStoredIds, initialized]);

  const filteredItems = useMemo(() => {
    const searchLower = search.toLowerCase();
    return items.filter((item) =>
      getItemLabel(item).toLowerCase().includes(searchLower),
    );
  }, [items, search, getItemLabel]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aSelected = value.some((v) => getItemId(v) === getItemId(a));
      const bSelected = value.some((v) => getItemId(v) === getItemId(b));

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return getItemLabel(a).localeCompare(getItemLabel(b));
    });
  }, [filteredItems, value, getItemId, getItemLabel]);

  const selectAllItems = () => {
    onChange(filteredItems);
  };

  const clearSelectedItems = () => {
    onChange([]);
  };

  const handleCheckboxChange = (item: T, checked: boolean) => {
    if (checked) {
      onChange([...value, item]);
    } else {
      onChange(value.filter((v) => getItemId(v) !== getItemId(item)));
    }
  };

  const selectedCount = value.length;
  const totalCount = items.length;
  const visibleCount = filteredItems.length;

  return (
    <Input.Wrapper {...props}>
      <Paper p="xs">
        <Stack gap="xs">
          <TextInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            disabled={disabled}
          />
          <Group justify="space-between">
            <Group>
              <Text>
                {selectedCount} of {totalCount} selected
              </Text>

              <BasicButton
                size="compact-xs"
                variant="outline"
                onClick={selectAllItems}
                disabled={disabled}
              >
                Select All
              </BasicButton>
              <BasicButton
                size="compact-xs"
                variant="subtle"
                onClick={clearSelectedItems}
                disabled={disabled}
              >
                Clear Selection
              </BasicButton>
            </Group>
            {search && (
              <Text>
                {visibleCount} of {totalCount} visible
              </Text>
            )}
          </Group>
          <ScrollArea h={150}>
            <Stack gap="xs">
              {sortedItems.map((item) => (
                <Checkbox
                  key={getItemId(item)}
                  label={getItemLabel(item)}
                  checked={value.some((v) => getItemId(v) === getItemId(item))}
                  onChange={(e) =>
                    handleCheckboxChange(item, e.currentTarget.checked)
                  }
                  disabled={disabled}
                />
              ))}
              {sortedItems.length === 0 && !disabled && (
                <Text ta="center" py="md">
                  No items found
                </Text>
              )}
            </Stack>
          </ScrollArea>
        </Stack>
      </Paper>
    </Input.Wrapper>
  );
}
